import { create } from "zustand";
import { devtools } from "zustand/middleware";
import firebase from "api/firebase/firebase";
import AppConfig from "components/App/AppConfig";

// App-wide locale state. Replaced the Redux `app` domain (previously an
// Immutable Map — state is plain JS now):
//   availableLanguages: [{ id, language }]  — the languages offered in the UI
//   selectedLanguage:   string             — seeded from the browser at load
//
// Seeding ported verbatim from the reducer: default to AppConfig.DEFAULT_LOCALE
// ("en") and override with the browser language when it reports one.
let languageUI = AppConfig.DEFAULT_LOCALE; // Default to English
// Get the browser language
const userLanguage = navigator.language || navigator.userLanguage;
if (userLanguage) {
  languageUI = userLanguage;
}

// The reducer's SET_AVAILABLE_LANGUAGES case: the firebase `locale` node is an
// id -> name object; the UI consumes it as an array of { id, language }.
const toLanguageList = (availableLanguages) =>
  Object.keys(availableLanguages).map((languageId) => ({
    id: languageId,
    language: availableLanguages[languageId],
  }));

const useAppStore = create(
  devtools(
    (set) => ({
      availableLanguages: [],
      selectedLanguage: languageUI,

      setAvailableLanguages: (availableLanguages) =>
        set(
          { availableLanguages: toLanguageList(availableLanguages) },
          false,
          "app/setAvailableLanguages",
        ),

      setSelectedLanguage: (selectedLanguage) =>
        set({ selectedLanguage }, false, "app/setSelectedLanguage"),

      // Ported from the `loadAvailableLanguages` redux thunk: read every locale
      // known to firebase, then narrow it to the locales the customization
      // config opts into (when it specifies any). Errors are logged and
      // swallowed exactly as before — a failing config falls back to the full
      // list, a failing locale read leaves the state untouched.
      loadAvailableLanguages: async () => {
        let availableLanguages;
        try {
          availableLanguages = await firebase.getAvailableLanguages();
        } catch (error) {
          console.error("Error getting available languages:", error);
          return;
        }

        try {
          const config = await firebase.getCustomizationConfig();

          // Show only the locales that are in the config (if present)
          function isContainedInConfig(languageAvailable) {
            return config.ui.showLocales.includes(languageAvailable.id);
          }

          try {
            if (config?.ui?.showLocales?.length) {
              // Convert the object to an array of objects
              const availableLanguagesArray = Object.entries(
                availableLanguages,
              ).map(([id, name]) => ({ id, name }));

              // Filter the array based on the configuration
              const filteredLanguagesArray =
                availableLanguagesArray.filter(isContainedInConfig);

              // Convert the filtered array back to an object (if needed)
              availableLanguages = filteredLanguagesArray.reduce(
                (obj, item) => {
                  obj[item.id] = item.name;
                  return obj;
                },
                {},
              );
            }
          } catch (error) {
            // Handle any errors that occur during the filtering process
            console.error(
              "An error occurred while filtering languages:",
              error,
            );
          }
        } catch (error) {
          console.error("Error getting customization config:", error);
        }

        set(
          { availableLanguages: toLanguageList(availableLanguages) },
          false,
          "app/loadAvailableLanguages",
        );
      },
    }),
    { name: "app" },
  ),
);

export default useAppStore;
